import { useCallback, useMemo, useState } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Repeat,
  Zap,
  Paperclip,
  Eye,
  FileText,
  CheckCircle2,
  CircleDashed,
  Clock,
  CircleDot,
  AlertTriangle,
  Columns2,
  Receipt,
  Send,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import type { MovimientoConCliente } from '@/types/database'
import { formatCurrency, formatDate } from '@/lib/utils'
import { calcularSaldosAcumulados, calcularTotales } from '@/lib/movimientos-utils'
import { useReciboPago } from '@/hooks/useReciboPago'
import { usePermissions } from '@/hooks/usePermissions'
import { MovimientosTableFooter } from './MovimientosTableFooter'
import { useFacturasDeMovimientos, usePuedeFacturar } from '@/hooks/useFacturas'
import type { FacturaResumen } from '@/services/facturasService'
import { useAccionesDeFactura } from '@/components/facturas/useAccionesDeFactura'
import { ConfirmarFacturaDialog, FacturaBadge, FacturaMenuItems } from '@/components/facturas/FacturaDeCobro'

interface MovimientosTableProps {
  movimientos: MovimientoConCliente[]
  onEditar: (movimiento: MovimientoConCliente) => void
  onEliminar: (movimiento: MovimientoConCliente) => void
  onVerComprobante?: (movimiento: MovimientoConCliente) => void
  isLoading?: boolean
  showCliente?: boolean
  showSaldo?: boolean
  /** When true, the running Saldo column nets partial payments (estado de cuenta).
   *  Default false keeps the gross debe - haber used by the Movimientos page. */
  saldoNeto?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

// Tipo extendido para incluir saldo calculado
type MovimientoExtendido = MovimientoConCliente & {
  _saldoAcumulado: number
}

// Componente TipoBadge
function TipoBadge({
  tipo,
  tipoDeuda,
}: {
  tipo: string
  tipoDeuda?: 'mensual' | 'esporadica' | null
}) {
  const variant = tipo === 'Deuda' ? 'destructive' : 'default'

  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={variant}>{tipo}</Badge>
      {tipo === 'Deuda' && tipoDeuda && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center h-5 w-5 rounded-full cursor-help ${
                tipoDeuda === 'mensual'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                  : 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300'
              }`}
            >
              {tipoDeuda === 'mensual' ? (
                <Repeat className="h-3 w-3" />
              ) : (
                <Zap className="h-3 w-3" />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {tipoDeuda === 'mensual'
              ? 'Deuda mensual recurrente'
              : 'Deuda esporádica (trabajo puntual)'}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

const APLICACION_CONFIG = {
  pendiente: { label: 'Pendiente', icon: Clock, color: 'text-amber-600 dark:text-amber-400' },
  parcial: { label: 'Parcial', icon: CircleDashed, color: 'text-blue-600 dark:text-blue-400' },
  pagado: { label: 'Pagado', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  sin_aplicar: { label: 'Sin aplicar', icon: CircleDot, color: 'text-muted-foreground' },
  aplicado_completo: { label: 'Aplicado', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
} as const

/** Constante para que la tabla no recalcule columnas mientras carga la consulta de facturas. */
const SIN_FACTURAS: Record<string, FacturaResumen> = {}

export function MovimientosTable({
  movimientos,
  onEditar,
  onEliminar,
  onVerComprobante,
  isLoading,
  showCliente = true,
  showSaldo = true,
  saldoNeto = false,
  canEdit = true,
  canDelete = true,
}: MovimientosTableProps) {
  const { descargar: descargarRecibo, enviar: enviarRecibo, generando } = useReciboPago()
  const { puede } = usePermissions()

  // Factura electrónica de cada cobro (ORR-255). La consulta se refresca sola mientras haya alguna en vuelo.
  const pagoIds = useMemo(() => movimientos.filter((m) => m.tipo === 'Pago').map((m) => m.id), [movimientos])
  const { data: facturasPorCobro = SIN_FACTURAS } = useFacturasDeMovimientos(pagoIds)
  const puedeFacturar = usePuedeFacturar()
  const { onAccion: onAccionFactura, dialogos: dialogosFactura, accionFactura } = useAccionesDeFactura()
  const [cobroAFacturar, setCobroAFacturar] = useState<MovimientoConCliente | null>(null)

  // El recibo se arma leyendo de la base (ORR-336), así que sirve tanto para un cobro recién
  // registrado como para uno viejo.
  const onDescargarRecibo = useCallback(
    async (movimiento: MovimientoConCliente) => {
      try {
        await descargarRecibo(movimiento.id)
      } catch (error) {
        console.error('Error generando el recibo de pago:', error)
        toast.error('No se pudo generar el recibo')
      }
    },
    [descargarRecibo],
  )

  // El envío automático del alta corre una sola vez y no deja rastro: si falla, el cobro quedaba
  // sin recibo y sin forma de mandarlo desde la app (ORR-394). Mismos datos que el automático —
  // salen del id del pago—, así que el recibo reenviado es idéntico al que debió salir.
  const onEnviarRecibo = useCallback(
    async (movimiento: MovimientoConCliente) => {
      const resultado = await enviarRecibo(movimiento.id)

      if (resultado.estado === 'sin-email') {
        toast.warning(`${resultado.clienteNombre} no tiene email cargado: el recibo no se envió`)
        return
      }
      if (resultado.estado === 'error') {
        toast.error('No se pudo enviar el recibo')
        return
      }
      toast.success('Recibo enviado al cliente')
    },
    [enviarRecibo],
  )

  // Calcular saldo acumulado por cliente (siempre calculamos para poder ordenar)
  const movimientosConSaldo = useMemo<MovimientoExtendido[]>(() => {
    const saldoPorMovimiento = calcularSaldosAcumulados(
      movimientos,
      (mov) => mov.cliente_id || mov.cliente?.id || mov.cliente?.persona?.nombre_completo || 'sin-cliente',
      undefined,
      saldoNeto,
    )

    return movimientos.map((mov) => ({
      ...mov,
      _saldoAcumulado: saldoPorMovimiento.get(mov.id) ?? 0,
    }))
  }, [movimientos, saldoNeto])

  // Calcular totales para el footer
  const totales = useMemo(() => calcularTotales(movimientos), [movimientos])

  // Toggle debe/haber separado vs monto unificado
  const [debeHaberSeparado, setDebeHaberSeparado] = useLocalStorage('movimientos:debeHaber', false)

  const amountColIds = new Set(['debe', 'haber', 'monto', 'saldo', 'acciones'])

  // Hook de preferencias de tabla
  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
  } = useTablePreferences('movimientos', {
    defaultVisibility: {
      fecha: true,
      cliente: showCliente,
      concepto: true,
      nro_comprobante: false,
      tipo: true,
      monto: true,
      saldo: showSaldo,
    },
  })

  // Definición de columnas
  const columns = useMemo<ColumnDef<MovimientoExtendido>[]>(
    () => [
      {
        id: 'created_at',
        accessorFn: (row) => row.created_at ? new Date(row.created_at).getTime() : 0,
        header: undefined,
        enableHiding: false,
      },
      {
        id: 'fecha',
        accessorFn: (row) => new Date(row.fecha).getTime(),
        header: 'Fecha',
        meta: { label: 'Fecha' },
        cell: ({ row }) => formatDate(row.original.fecha),
      },
      ...(showCliente
        ? [
            {
              id: 'cliente',
              accessorFn: (row: MovimientoExtendido) =>
                row.cliente?.persona?.nombre_completo || '',
              header: 'Cliente',
              meta: { label: 'Cliente' },
              cell: ({ row }: { row: { original: MovimientoExtendido } }) => (
                <span className="font-medium">
                  {row.original.cliente?.persona?.nombre_completo || '-'}
                </span>
              ),
            } as ColumnDef<MovimientoExtendido>,
          ]
        : []),
      {
        id: 'concepto',
        accessorKey: 'concepto',
        header: 'Concepto',
        meta: { label: 'Concepto' },
        cell: ({ row }) => {
          const mov = row.original
          return (
            <div className="flex flex-col">
              <span>{mov.concepto}</span>
              {mov.mes_anio && (
                <span className="text-xs text-muted-foreground">{mov.mes_anio}</span>
              )}
              {mov.observaciones && (
                <span className="text-xs text-muted-foreground italic">
                  {mov.observaciones}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'nro_comprobante',
        accessorFn: (row) => row.comprobante_numero || '',
        header: 'N. Comprobante',
        meta: { label: 'N. Comprobante' },
        cell: ({ row }) => {
          const numero = row.original.comprobante_numero
          if (!numero) return <span className="text-muted-foreground">-</span>
          return (
            <span className="font-mono text-sm tabular-nums">
              {numero}
            </span>
          )
        },
      },
      {
        id: 'tipo',
        accessorKey: 'tipo',
        header: 'Tipo',
        meta: { label: 'Tipo' },
        cell: ({ row }) => {
          const mov = row.original
          return (
            <div className="flex items-center gap-1.5">
              <TipoBadge tipo={mov.tipo} tipoDeuda={mov.tipo_deuda as 'mensual' | 'esporadica' | null} />
              {mov.tipo === 'Pago' && <FacturaBadge factura={facturasPorCobro[mov.id]} />}
              {mov._adjuntoDocumentoId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="Ver comprobante adjunto"
                      className="inline-flex p-0 border-0 bg-transparent cursor-pointer"
                      onClick={() => onVerComprobante?.(mov)}
                    >
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Comprobante adjunto - click para ver</TooltipContent>
                </Tooltip>
              )}
              {mov.tipo === 'Deuda' &&
                mov.fecha_vencimiento &&
                new Date(`${mov.fecha_vencimiento}T00:00:00`) < new Date(new Date().toISOString().split('T')[0] + 'T00:00:00') &&
                (mov._aplicacionEstado === 'pendiente' || mov._aplicacionEstado === 'parcial') && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Vencido desde {formatDate(mov.fecha_vencimiento)}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      ...(debeHaberSeparado
        ? [
            {
              id: 'debe',
              accessorFn: (row: MovimientoExtendido) => row.debe ?? 0,
              header: 'Debe',
              meta: { label: 'Debe', headerClassName: 'text-right' },
              cell: ({ row }: { row: { original: MovimientoExtendido } }) => {
                const mov = row.original
                const debe = mov.debe ?? 0
                if (debe === 0) return <span className="block text-right">-</span>

                const estado = mov._aplicacionEstado
                const config = estado ? APLICACION_CONFIG[estado] : null
                const Icon = config?.icon

                return (
                  <div className="flex flex-col items-end">
                    <span className="font-medium tabular-nums text-red-600">
                      {formatCurrency(debe)}
                    </span>
                    {config && Icon && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className={`inline-flex items-center gap-1 text-xs bg-transparent border-0 p-0 cursor-help ${config.color}`}>
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {(mov._aplicacionMontoAplicado ?? 0) > 0 && (
                            <div>Aplicado: {formatCurrency(mov._aplicacionMontoAplicado!)}</div>
                          )}
                          {(estado === 'pendiente' || estado === 'parcial') && (
                            <div>Pendiente: {formatCurrency(mov._aplicacionSaldoPendiente ?? 0)}</div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )
              },
            } as ColumnDef<MovimientoExtendido>,
            {
              id: 'haber',
              accessorFn: (row: MovimientoExtendido) => row.haber ?? 0,
              header: 'Haber',
              meta: { label: 'Haber', headerClassName: 'text-right' },
              cell: ({ row }: { row: { original: MovimientoExtendido } }) => {
                const mov = row.original
                const haber = mov.haber ?? 0
                if (haber === 0) return <span className="block text-right">-</span>

                const estado = mov._aplicacionEstado
                const config = estado ? APLICACION_CONFIG[estado] : null
                const Icon = config?.icon

                return (
                  <div className="flex flex-col items-end">
                    <span className="font-medium tabular-nums text-green-600">
                      {formatCurrency(haber)}
                    </span>
                    {config && Icon && estado === 'sin_aplicar' && (mov._aplicacionSaldoPendiente ?? 0) > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className={`inline-flex items-center gap-1 text-xs bg-transparent border-0 p-0 cursor-help ${config.color}`}>
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div>A favor: {formatCurrency(mov._aplicacionSaldoPendiente!)}</div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )
              },
            } as ColumnDef<MovimientoExtendido>,
          ]
        : [
            {
              id: 'monto',
              accessorFn: (row: MovimientoExtendido) => {
                const debe = row.debe ?? 0
                const haber = row.haber ?? 0
                return debe > 0 ? debe : haber
              },
              header: 'Monto',
              meta: { label: 'Monto', headerClassName: 'text-right' },
              cell: ({ row }: { row: { original: MovimientoExtendido } }) => {
                const mov = row.original
                const debe = mov.debe ?? 0
                const haber = mov.haber ?? 0
                const isDebe = debe > 0
                const monto = isDebe ? debe : haber

                if (monto === 0) return <span className="block text-right">-</span>

                const estado = mov._aplicacionEstado
                const config = estado ? APLICACION_CONFIG[estado] : null
                const Icon = config?.icon

                return (
                  <div className="flex flex-col items-end">
                    <span
                      className={`font-medium tabular-nums ${
                        isDebe ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {formatCurrency(monto)}
                    </span>
                    {config && Icon && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className={`inline-flex items-center gap-1 text-xs bg-transparent border-0 p-0 cursor-help ${config.color}`}>
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {(mov._aplicacionMontoAplicado ?? 0) > 0 && (
                            <div>Aplicado: {formatCurrency(mov._aplicacionMontoAplicado!)}</div>
                          )}
                          {(estado === 'pendiente' || estado === 'parcial') && (
                            <div>Pendiente: {formatCurrency(mov._aplicacionSaldoPendiente ?? 0)}</div>
                          )}
                          {estado === 'sin_aplicar' && (mov._aplicacionSaldoPendiente ?? 0) > 0 && (
                            <div>A favor: {formatCurrency(mov._aplicacionSaldoPendiente!)}</div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )
              },
            } as ColumnDef<MovimientoExtendido>,
          ]),
      ...(showSaldo
        ? [
            {
              id: 'saldo',
              accessorFn: (row: MovimientoExtendido) => row._saldoAcumulado ?? 0,
              header: 'Saldo',
              meta: { label: 'Saldo', headerClassName: 'text-right' },
              cell: ({ row }: { row: { original: MovimientoExtendido } }) => {
                const saldo = row.original._saldoAcumulado ?? 0
                return (
                  <span
                    className={`block text-right font-medium tabular-nums ${
                      saldo > 0
                        ? 'text-red-600'
                        : saldo < 0
                          ? 'text-green-600'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {formatCurrency(Math.abs(saldo))}
                    {saldo < 0 && <span className="text-xs ml-1">(a favor)</span>}
                  </span>
                )
              },
            } as ColumnDef<MovimientoExtendido>,
          ]
        : []),
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        size: 50,
        cell: ({ row }) => {
          const movimiento = row.original
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem onClick={() => onEditar(movimiento)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {movimiento._adjuntoDocumentoId && onVerComprobante && (
                  <DropdownMenuItem onClick={() => onVerComprobante(movimiento)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver Adjunto
                  </DropdownMenuItem>
                )}
                {movimiento.tipo === 'Pago' && (
                  <DropdownMenuItem onClick={() => onDescargarRecibo(movimiento)}>
                    <Receipt className="mr-2 h-4 w-4" />
                    Descargar recibo
                  </DropdownMenuItem>
                )}
                {movimiento.tipo === 'Pago' && puede.enviarRecibo && (
                  <DropdownMenuItem
                    disabled={generando}
                    onClick={() => onEnviarRecibo(movimiento)}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Enviar recibo por email
                  </DropdownMenuItem>
                )}
                {movimiento.tipo === 'Pago' && puede.verFacturas && (
                  <FacturaMenuItems
                    factura={facturasPorCobro[movimiento.id]}
                    puedeFacturar={puedeFacturar}
                    puedeAnular={puede.anularFactura}
                    puedeEliminarCobro={puede.eliminarMovimiento}
                    onEmitir={() => setCobroAFacturar(movimiento)}
                    onAccion={onAccionFactura}
                  />
                )}
                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => onEliminar(movimiento)}
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    [showCliente, showSaldo, debeHaberSeparado, onEditar, onEliminar, onVerComprobante, onDescargarRecibo, onEnviarRecibo, puede.enviarRecibo, puede.verFacturas, puede.anularFactura, puede.eliminarMovimiento, generando, canEdit, canDelete, facturasPorCobro, puedeFacturar, onAccionFactura]
  )

  // Instancia de tabla TanStack
  const table = useReactTable({
    data: movimientosConSaldo,
    columns,
    state: {
      columnVisibility: { ...visibility, created_at: false },
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageSize: 50 },
    },
  })

  return (
    <TooltipProvider delayDuration={100}>
      <div className="h-full flex flex-col gap-4">
        {/* Toolbar */}
        <div className="shrink-0 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDebeHaberSeparado(!debeHaberSeparado)}
          >
            <Columns2 className="h-4 w-4 mr-1" />
            {debeHaberSeparado ? 'Unificar' : 'Separar D/H'}
          </Button>
          <ColumnVisibilityMenu table={table} onReset={resetVisibility} />
        </div>

        {/* Tabla */}
        <DataTable
          className="flex-1 min-h-0"
          table={table}
          isLoading={isLoading}
          emptyMessage="No hay movimientos para mostrar"
          emptyIcon={<FileText className="h-6 w-6 text-muted-foreground" />}
          showPagination
          enableColumnResizing
          enableVerticalScroll
          stickyRightColumns={['acciones']}
          footerContent={
            movimientos.length > 0 ? (
              <MovimientosTableFooter
                totalDebe={totales.totalDebe}
                totalHaber={totales.totalHaber}
                saldo={totales.saldo}
                leadingColSpan={table.getVisibleLeafColumns().filter(c => !amountColIds.has(c.id)).length}
                showSaldo={showSaldo}
                debeHaberSeparado={debeHaberSeparado}
              />
            ) : undefined
          }
        />
      </div>
      {cobroAFacturar && (
        <ConfirmarFacturaDialog
          open
          onOpenChange={(abierto) => !abierto && setCobroAFacturar(null)}
          clienteNombre={cobroAFacturar.cliente?.persona?.nombre_completo || 'el cliente'}
          monto={cobroAFacturar.haber ?? cobroAFacturar.monto ?? 0}
          onConfirmar={(enviarEmail) => {
            accionFactura.mutate(
              { tipo: 'emitirDesdeCobro', movimientoId: cobroAFacturar.id, enviarEmail },
              { onSuccess: () => toast.success('Factura pedida: se emite en unos segundos') },
            )
            setCobroAFacturar(null)
          }}
        />
      )}
      {dialogosFactura}
    </TooltipProvider>
  )
}
